import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Typography } from '@heroui/react';
import {
    LabelListDownloadButton,
    countParam,
    type LabelListDownload,
} from '@/pages/catalogImport';
import { getNodeTestIdProp } from '@/utils';
import { labelUnitsOf, type BarcodeIssuesSuccess } from './barcodeIssuesMeta';

const COPY = 'products.barcodeIssues';

type BarcodeIssuesSuccessAlertProps = {
    success: BarcodeIssuesSuccess;
    download: LabelListDownload;
};

/**
 * A fix that went through (AC-09): how many barcodes were fixed and, when
 * some were generated, "N new barcodes: M labels to print" with **Download
 * label list (.xlsx)**, built from the call's `generatedBarcodes`.
 */
export const BarcodeIssuesSuccessAlert: React.FC<
    BarcodeIssuesSuccessAlertProps
> = ({ success, download }) => {
    const { t } = useTranslation('pages');
    const { t: tc } = useTranslation('components');
    const generated = success.generatedBarcodes.length;
    const labels = labelUnitsOf(success.generatedBarcodes);

    return (
        <Alert
            status="success"
            {...getNodeTestIdProp('barcode-issues-success')}
        >
            <Alert.Indicator />
            <Alert.Content>
                <Alert.Title>
                    {t(`${COPY}.success.fixed`, {
                        count: success.fixed,
                        fixed: countParam(success.fixed),
                    })}
                </Alert.Title>
                {generated > 0 ? (
                    <>
                        <Alert.Description
                            {...getNodeTestIdProp(
                                'barcode-issues-success-labels',
                            )}
                        >
                            {tc('labelList.summary', {
                                barcodes: tc('labelList.newBarcodes', {
                                    count: generated,
                                    barcodes: countParam(generated),
                                }),
                                labels: tc('labelList.labelsToPrint', {
                                    count: labels,
                                    labels: countParam(labels),
                                }),
                            })}
                        </Alert.Description>
                        <Alert.Description>
                            {t(`${COPY}.success.description`)}
                        </Alert.Description>
                        {download.downloadFailed ? (
                            <Typography
                                color="muted"
                                type="body-xs"
                                {...getNodeTestIdProp(
                                    'barcode-issues-download-failed',
                                )}
                            >
                                {tc('labelList.downloadFailed')}
                            </Typography>
                        ) : null}
                    </>
                ) : null}
            </Alert.Content>
            {generated > 0 ? (
                <LabelListDownloadButton
                    download={download}
                    size="sm"
                    testId="barcode-issues-download"
                />
            ) : null}
        </Alert>
    );
};
